import {
  ApiResponse,
  BrowseResult,
  MediaInfo,
  PresignedUrlResponse,
  UploadResponse,
  UploadedFile,
  DeleteResult,
  DeleteBatchResult,
  CreateDirectoryRequest,
  CreateDirectoryResponse,
  DeleteDirectoryRequest,
  DirectoryOperationResult,
  DirectoryExistsResponse,
} from "../types/api";
import { 
  Note, 
  CreateNoteRequest, 
  UpdateNoteRequest,
  NoteContentChunk 
} from "../types/note";
import { PIN_SESSION_STORAGE_KEY } from "../hooks/usePinGate";

// The API is served same-origin via Pages Functions (/api/*). Auth rides on
// HttpOnly cookies — no token in JS-readable storage.
// (VITE_API_BASE_URL may still point at the workers.dev API for local dev
// against `wrangler dev`.)
const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL || "";

/** Same-origin API base for direct fetches from components. */
export const apiBase = (): string => API_BASE_URL;

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = "ApiError";
  }
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  try {
    // Get JWT token from localStorage as fallback for cookie issues
    const token = localStorage.getItem("jwt_token");

    // Get PIN session token (supports IP-restricted environments)
    const pinSession = localStorage.getItem(PIN_SESSION_STORAGE_KEY);

    // Build headers - start with defaults
    const finalHeaders = new Headers({
      "Content-Type": "application/json",
    });

    // Add existing headers if they exist
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          finalHeaders.set(key, value);
        });
      } else {
        Object.entries(options.headers).forEach(([key, value]) => {
          finalHeaders.set(key, value);
        });
      }
    }

    // Add Authorization header if token exists
    if (token) {
      finalHeaders.set("Authorization", `Bearer ${token}`);
    }
    if (pinSession) {
      finalHeaders.set("X-Pin-Session", pinSession);
    }

    const response = await fetch(url, {
      credentials: "include", // Include cookies for authentication
      headers: finalHeaders,
      ...options,
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Unknown error" }));
      throw new ApiError(response.status, errorData.error || "Request failed");
    }

    return await response.json();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(
      0,
      error instanceof Error ? error.message : "Network error"
    );
  }
}

export const api = {
  /**
   * Browse files and folders at a given path
   */
  async browse(path: string = ""): Promise<BrowseResult> {
    const cleanPath = path.startsWith("/") ? path.slice(1) : path;
    const response = await apiRequest<ApiResponse<BrowseResult>>(
      `/api/browse${cleanPath ? `?path=${encodeURIComponent(cleanPath)}` : ""}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to browse files");
    }

    return response.data;
  },

  /**
   * Get detailed information about a media file
   */
  async getMediaInfo(key: string): Promise<MediaInfo> {
    const response = await apiRequest<ApiResponse<MediaInfo>>(
      `/api/browse/info/${encodeURIComponent(key)}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to get media info");
    }

    return response.data;
  },

  /**
   * Get a pre-signed URL for a media file
   */
  async getPresignedUrl(
    key: string,
    expires?: number
  ): Promise<PresignedUrlResponse> {
    const params = expires ? `?expires=${expires}` : "";
    const response = await apiRequest<ApiResponse<PresignedUrlResponse>>(
      `/api/media/${encodeURIComponent(key)}/presigned${params}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to get presigned URL");
    }

    return response.data;
  },

  /**
   * Get the direct URL for a media file
   */
  getMediaUrl(key: string, usePresigned = false): string {
    const params = usePresigned ? "?presigned=true" : "";
    return `${API_BASE_URL}/api/media/${encodeURIComponent(key)}${params}`;
  },

  /**
   * Get the direct URL for a thumbnail
   */
  getThumbnailUrl(key: string): string {
    return `${API_BASE_URL}/api/thumbnail/${encodeURIComponent(key)}`;
  },

  /**
   * Fetch media file as blob with authentication
   */
  async getMediaBlob(key: string): Promise<Blob> {
    const token = localStorage.getItem("jwt_token");
    const headers = new Headers();

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const pinSession = localStorage.getItem(PIN_SESSION_STORAGE_KEY);
    if (pinSession) {
      headers.set("X-Pin-Session", pinSession);
    }

    const response = await fetch(
      `${API_BASE_URL}/api/media/${encodeURIComponent(key)}`,
      {
        credentials: "include",
        headers,
      }
    );

    if (!response.ok) {
      throw new ApiError(
        response.status,
        `Failed to fetch media: ${response.statusText}`
      );
    }

    return await response.blob();
  },

  /**
   * Fetch thumbnail as blob with authentication
   */
  async getThumbnailBlob(key: string): Promise<Blob> {
    const token = localStorage.getItem("jwt_token");
    const headers = new Headers();

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const pinSession = localStorage.getItem(PIN_SESSION_STORAGE_KEY);
    if (pinSession) {
      headers.set("X-Pin-Session", pinSession);
    }

    const response = await fetch(
      `${API_BASE_URL}/api/thumbnail/${encodeURIComponent(key)}`,
      {
        credentials: "include",
        headers,
      }
    );

    if (!response.ok) {
      throw new ApiError(
        response.status,
        `Failed to fetch thumbnail: ${response.statusText}`
      );
    }

    return await response.blob();
  },

  /**
   * Get authenticated media URL as blob URL
   */
  async getAuthenticatedMediaUrl(key: string): Promise<string> {
    const blob = await this.getMediaBlob(key);
    return URL.createObjectURL(blob);
  },

  /**
   * Get authenticated thumbnail URL as blob URL
   */
  async getAuthenticatedThumbnailUrl(key: string): Promise<string> {
    const blob = await this.getThumbnailBlob(key);
    return URL.createObjectURL(blob);
  },

  /**
   * Upload multiple files to a specified folder
   */
  async uploadFiles(
    files: FileList,
    path: string = "",
    thumbs?: Map<string, Blob>,
  ): Promise<UploadResponse> {
    const formData = new FormData();

    // Add all files
    Array.from(files).forEach((file) => {
      formData.append(`files`, file);
    });

    // Client-generated thumbnails (see lib/thumbnail.ts) ride along so the
    // backend can store .thumbs/<key>.webp|jpg in the same request
    if (thumbs) {
      for (const [name, blob] of thumbs) {
        formData.append(`thumbs_${name}`, blob, name);
      }
    }

    // Add path if provided
    if (path) {
      formData.append("path", path);
    }

    const response = await fetch(`${API_BASE_URL}/api/upload`, {
      method: "POST",
      credentials: "include",
      headers: {
        // Don't set Content-Type header for FormData, let browser set it with boundary
        ...(localStorage.getItem("jwt_token")
          ? { Authorization: `Bearer ${localStorage.getItem("jwt_token")}` }
          : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Upload failed" }));
      throw new ApiError(response.status, errorData.error || "Upload failed");
    }

    const result = await response.json();

    if (!result.success || !result.data) {
      throw new Error(result.error || "Upload failed");
    }

    return result.data;
  },

  /**
   * Upload a single file
   */
  async uploadFile(file: File, path: string = ""): Promise<UploadedFile> {
    const formData = new FormData();
    formData.append("file", file);

    if (path) {
      formData.append("path", path);
    }

    const response = await fetch(`${API_BASE_URL}/api/upload/single`, {
      method: "POST",
      credentials: "include",
      headers: {
        ...(localStorage.getItem("jwt_token")
          ? { Authorization: `Bearer ${localStorage.getItem("jwt_token")}` }
          : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: "Upload failed" }));
      throw new ApiError(response.status, errorData.error || "Upload failed");
    }

    const result = await response.json();

    if (!result.success || !result.data) {
      throw new Error(result.error || "Upload failed");
    }

    return result.data;
  },

  /**
   * Delete a single media file
   */
  async deleteMedia(key: string): Promise<DeleteResult> {
    if (!key) {
      throw new Error("Media key is required for deletion");
    }

    const response = await apiRequest<DeleteResult>(
      `/api/delete/single?key=${encodeURIComponent(key)}`,
      {
        method: "DELETE",
      }
    );

    return response;
  },

  /**
   * Delete multiple media files
   */
  async deleteMediaBatch(keys: string[]): Promise<DeleteBatchResult> {
    if (!keys || !Array.isArray(keys) || keys.length === 0) {
      throw new Error("Keys array is required and must not be empty");
    }

    // Validate batch size
    if (keys.length > 50) {
      throw new Error("Batch size cannot exceed 50 items");
    }

    // Validate each key
    for (const key of keys) {
      if (!key || typeof key !== "string") {
        throw new Error(`Invalid key: ${key}`);
      }
    }

    const response = await apiRequest<DeleteBatchResult>("/api/delete/batch", {
      method: "DELETE",
      body: JSON.stringify({ keys }),
    });

    return response;
  },

  /**
   * Create a new directory
   */
  async createDirectory(
    path: string,
    name: string
  ): Promise<CreateDirectoryResponse> {
    const response = await apiRequest<ApiResponse<CreateDirectoryResponse>>(
      "/api/directories/create",
      {
        method: "POST",
        body: JSON.stringify({ path, name } as CreateDirectoryRequest),
      }
    );

    // Backend returns the create payload at the top level (no `data` wrapper)
    if (!response.success) {
      throw new Error(response.error || "Failed to create directory");
    }

    return response as unknown as CreateDirectoryResponse;
  },

  /**
   * Delete a directory (and optionally all contents)
   */
  async deleteDirectory(
    path: string,
    recursive = false
  ): Promise<DirectoryOperationResult> {
    const response = await apiRequest<ApiResponse<DirectoryOperationResult>>(
      "/api/directories/delete",
      {
        method: "DELETE",
        body: JSON.stringify({ path, recursive } as DeleteDirectoryRequest),
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to delete directory");
    }

    return response.data;
  },

  /**
   * Check if a directory exists
   */
  async checkDirectoryExists(path: string): Promise<boolean> {
    const response = await apiRequest<DirectoryExistsResponse>(
      `/api/directories/exists?path=${encodeURIComponent(path)}`
    );

    if (!response.success) {
      throw new Error(response.error || "Failed to check directory existence");
    }

    return response.exists;
  },

  /**
   * Get all notes for the authenticated user
   */
  async getNotes(): Promise<Note[]> {
    const response = await apiRequest<ApiResponse<Note[]>>("/api/notes");
    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to fetch notes");
    }
    return response.data;
  },

  /**
   * Fetch a chunk of a note's content for incremental rendering.
   * Server cuts at a safe markdown block boundary.
   */
  async getNoteContent(
    id: number,
    offset = 0,
    len = 65536,
  ): Promise<NoteContentChunk> {
    const response = await apiRequest<ApiResponse<NoteContentChunk>>(
      `/api/notes/${id}/content?offset=${offset}&len=${len}`,
    );
    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to fetch note content");
    }
    return response.data;
  },

  /**
   * Create a new note
   */
  async createNote(request: CreateNoteRequest): Promise<Note> {
    const response = await apiRequest<ApiResponse<Note>>("/api/notes", {
      method: "POST",
      body: JSON.stringify(request),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to create note");
    }
    return response.data;
  },

  /**
   * Update an existing note
   */
  async updateNote(id: number, request: UpdateNoteRequest): Promise<Note> {
    const response = await apiRequest<ApiResponse<Note>>(`/api/notes/${id}`, {
      method: "PUT",
      body: JSON.stringify(request),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || "Failed to update note");
    }
    return response.data;
  },

  /**
   * Delete a note
   */
  async deleteNote(id: number): Promise<void> {
    const response = await apiRequest<{ success: boolean; error?: string }>(
      `/api/notes/${id}`,
      {
        method: "DELETE",
      }
    );
    if (!response.success) {
      throw new Error(response.error || "Failed to delete note");
    }
  },
};

export { ApiError };
